import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import * as Field from '../../../../Common/Component/Field';
import { Base } from '../../../../Common/Component/Field';
import './index.scss';
import ReactDatePicker from 'react-datepicker';
import Swal from 'sweetalert2';
import * as request from '../../../../Common/Util/Request';
import { Button } from '@mui/material';
import { isFreeTx } from '../../../../Common/Util/Transaction';
import { formatUserClassScheduleLabel } from '../../../../Common/Util/Schedule';
import OptionChangeModal from './OptionChangeModal';

function LectureUserDetail(props) {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const lectureUser = location.state.user;
  const lectures = useMemo(
    () =>
      lectureUser.FK_class.class_type === 'single'
        ? [lectureUser.FK_class]
        : lectureUser.FK_class.ClassSingle_ClassPackages.map(
            (ClassSingle_ClassPackage) => ClassSingle_ClassPackage.FK_class_single,
          ),
    [lectureUser],
  );
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isOnline, setIsOnline] = useState(true);
  const isOffline = lectureUser.FK_class.listen_type === 'offline';
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [optionData, setOptionData] = useState(null);
  const [optionLoading, setOptionLoading] = useState(false);
  const [scheduleLabel, setScheduleLabel] = useState(() => formatUserClassScheduleLabel(lectureUser, intl));
  const isFree = isFreeTx(lectureUser.FK_tx);
  const isCanFreeCancel = lectureUser.FK_tx.status === 'paid' && !lectureUser.FK_tx.deletedAt && isFree;
  useEffect(() => {
    setEndDate(new Date(lectureUser.end_date));
    setStartDate(new Date(lectureUser.start_date));
    setIsOnline(lectureUser.FK_class.listen_type === 'online' || lectureUser.FK_class.listen_type === 'foreigner');
  }, [lectureUser]);

  const updateEndDate = useCallback(() => {
    const hasModified = moment(endDate).format('YYYY-MM-DD') !== moment(lectureUser.end_date).format('YYYY-MM-DD');
    if (hasModified) {
      Swal.fire({
        title: `수강종료일을 ${moment(endDate).format('YYYY-MM-DD')}로 수정하시겠습니까?`,
        text: '확인 시 즉시 반영됩니다.',
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
        reverseButtons: true,
        showLoaderOnConfirm: true,
        preConfirm: async (data) => {
          const result = await request.updateEndDate(lectureUser.id, endDate);
          return new Promise((resolve) => {
            setTimeout(() => {
              if (result.ok) {
                Swal.fire({
                  text: '수강종료일이 수정되었습니다.',
                  type: 'success',
                  icon: 'success',
                  confirmButtonText: '확인',
                  toast: true,
                  timer: 2000,
                });
                lectureUser.end_date = endDate;
              }
              resolve();
            }, 1000);
          });
        },
      });
    } else {
      Swal.fire({
        text: '수강종료일이 수정되지 않았습니다.',
        type: 'error',
        icon: 'error',
        confirmButtonText: '확인',
        toast: true,
      });
    }
  }, [endDate, lectureUser]);
  const loadOptionCandidates = useCallback(async () => {
    setOptionLoading(true);
    try {
      const result = await request.getUserClassOptionCandidates(lectureUser.id);
      if (!result || !result.ok) {
        return null;
      }
      return result.data;
    } catch (error) {
      // 인터셉터가 detail 을 alert 으로 노출한다.
      return null;
    } finally {
      setOptionLoading(false);
    }
  }, [lectureUser]);

  const openOptionModal = useCallback(async () => {
    const data = await loadOptionCandidates();
    if (!data) {
      return;
    }
    if (data.changeable === false) {
      Swal.fire({
        icon: 'warning',
        text: data.blockedReason?.reasonText || '이 수강 건은 옵션을 변경할 수 없습니다.',
        confirmButtonText: '확인',
      });
      return;
    }
    if (!data.candidates || data.candidates.length === 0) {
      Swal.fire({
        icon: 'info',
        text: '변경 가능한 다른 옵션이 없습니다.',
        confirmButtonText: '확인',
      });
      return;
    }
    setOptionData(data);
    setOptionModalOpen(true);
  }, [loadOptionCandidates]);

  const reloadOptionCandidates = useCallback(async () => {
    const data = await loadOptionCandidates();
    if (data) {
      setOptionData(data);
    }
  }, [loadOptionCandidates]);

  const onOptionChangeSuccess = useCallback(
    (result) => {
      const after = result?.after;
      if (after) {
        // 응답 after 는 { id, label } 뿐이므로, 스케줄 전체 필드는 모달에 띄웠던 후보 데이터에서 가져온다.
        const nextSchedule = (optionData?.candidates || []).find((candidate) => candidate.id === after.id);
        // location.state.user 는 목록에서 넘어온 스냅샷이다.
        // 화면 값과 함께 라우터 state 까지 갱신해야 뒤로가기 후 재진입 시 낡은 옵션이 보이지 않는다.
        lectureUser.FK_class_offline_schedule_id = after.id;
        if (nextSchedule) {
          lectureUser.FK_class_offline_schedule = nextSchedule;
        }
        setScheduleLabel(after.label || formatUserClassScheduleLabel(lectureUser, intl));
        history.replace(location.pathname, { ...location.state, user: lectureUser });
      }
      setOptionModalOpen(false);
      setOptionData(null);
      Swal.fire({
        text: '수강 옵션이 변경되었습니다.',
        icon: 'success',
        confirmButtonText: '확인',
        toast: true,
        timer: 2000,
      });
    },
    [history, intl, lectureUser, location.pathname, location.state, optionData],
  );

  const txStatus = useMemo(
    () => ({
      ready: '결제대기', // 무통장입금대기
      paid: '결제',
      cancelled: isFree ? '취소' : '환불',
      partial_cancelled: '부분환불',
    }),
    [isFree],
  );

  const handleRefund = useCallback(() => {
    Swal.fire({
      icon: 'warning',
      title: '무료 결제 강의 취소',
      text: '무료 결제 강의를 취소하시겠습니까? 취소 시 수강생은 해당 강의를 더 이상 이용할 수 없습니다.',
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#999',
      focusCancel: true,
      focusConfirm: false,
      backdrop: 'rgba(0,0,0,0.5)',
      iconColor: '#d33',
      reverseButtons: true,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const result = await request.cancelFreeLectureUser(lectureUser.id);
          if (result.success) {
            return { success: true };
          } else {
            throw new Error(result.message || '강의 취소에 실패했습니다.');
          }
        } catch (error) {
          Swal.showValidationMessage(`오류: ${error.message}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value?.success) {
        Swal.fire({
          text: '무료 결제 강의가 취소되었습니다.',
          icon: 'success',
          confirmButtonText: '확인',
          toast: true,
          timer: 2000,
        }).then(() => {
          window.history.back();
        });
      }
    });
  }, [lectureUser]);

  return (
    <div className="lecture-user">
      <Field.Input title="회원ID" value={lectureUser.FK_user.id} fixed />
      <Field.Input title="닉네임" value={lectureUser.FK_user.nickname} fixed />
      <Field.Input title="이름" value={lectureUser.FK_user.name} fixed />
      <Field.Input title="국가 정보" value={lectureUser.FK_user.FK_country?.name_kor || '-'} fixed />
      <Field.Input title="강의ID" value={lectureUser.FK_class.id} fixed />
      <Field.Input title="강의명" value={lectureUser.FK_class.title} fixed />
      <Field.Input title="수강옵션" value={scheduleLabel} fixed />
      {isOffline && (
        <Field.Base title="수강옵션 변경">
          <Button variant="outlined" onClick={openOptionModal} disabled={optionLoading}>
            옵션 변경
          </Button>
        </Field.Base>
      )}
      <Field.Input
        title="강사명"
        value={lectureUser.FK_class.Class_Tutors.map((Class_Tutor) => Class_Tutor.FK_tutor.name).join(',')}
        fixed
      />
      <Field.Input
        title="구분"
        value={<FormattedMessage id={'ID_LISTEN_TYPE_' + lectureUser.FK_class.listen_type.toUpperCase()} />}
        fixed
      />
      <Field.Input
        title="결제일"
        value={lectureUser.FK_tx.deletedAt ? '-' : moment.utc(lectureUser.FK_tx.date_paid_at).format('YYYY-MM-DD')}
        fixed
      />
      <Field.Input
        title="콘텐츠 지급일"
        value={lectureUser.FK_tx.deletedAt ? moment.utc(lectureUser.FK_tx.createdAt).format('YYYY-MM-DD') : '-'}
        fixed
      />
      <Field.Input
        title="수강시작일"
        value={lectureUser.start_date ? moment.utc(lectureUser.start_date).format('YYYY-MM-DD') : '-'}
        fixed
      />
      {isOnline ? (
        <Base title="수강종료일">
          {/*{endDate}*/}
          {endDate && endDate !== '-' && (
            <div>
              <ReactDatePicker selected={endDate} minDate={startDate} dateFormat="yyyy-MM-dd" onChange={setEndDate} />
            </div>
          )}
          <button style={{ marginLeft: '12px' }} onClick={() => updateEndDate()}>
            저장
          </button>
        </Base>
      ) : (
        <Field.Input
          title="수강종료일"
          value={lectureUser.end_date ? moment.utc(lectureUser.end_date).format('YYYY-MM-DD') : '-'}
          fixed
        />
      )}

      <Field.Input
        title="남은일수"
        value={lectureUser.count_left_day ? lectureUser.count_left_day + '일' : '-'}
        fixed
      />
      <Field.Input title="금액(정가)" value={lectureUser.FK_class.price_original + '₩'} fixed />
      <Field.Input title="결제금액" value={lectureUser.FK_tx.paid_amount + '₩'} fixed />
      <Field.Input
        title="강의자료 다운로드"
        value={lectureUser.is_download_attached_file ? '다운로드 후' : '다운로드 전'}
        fixed
      />
      <Field.Input title="진도율" value={`${lectureUser.completion_rate ? lectureUser.completion_rate : 0}%`} fixed />
      <Field.Base title="차시별 진도율">
        <ul>
          {lectures.map((lecture) =>
            lecture.ClassChapters.map((ClassChapter, chapterIndex) =>
              ClassChapter.ClassSections.map((ClassSection, sectionIndex) => {
                const key = `${lecture.id}-${chapterIndex + 1}-${sectionIndex + 1}`;
                return (
                  <li key={key}>
                    {key}
                    {` `}
                    {ClassSection.section_completion_rate ? `${ClassSection.section_completion_rate}` : '0'}%
                  </li>
                );
              }),
            ),
          )}
        </ul>
      </Field.Base>
      <Field.Input
        title="결제 상태"
        value={lectureUser.FK_tx.status ? txStatus[lectureUser.FK_tx.status] : '-'}
        fixed
      />
      {isFree && (
        <Field.Base>
          <Button onClick={handleRefund} variant="contained" color="error" disabled={!isCanFreeCancel}>
            취소 처리
          </Button>
        </Field.Base>
      )}
      {optionModalOpen && (
        <OptionChangeModal
          open={optionModalOpen}
          data={optionData}
          userClassId={lectureUser.id}
          onClose={() => {
            setOptionModalOpen(false);
            setOptionData(null);
          }}
          onReload={reloadOptionCandidates}
          onSuccess={onOptionChangeSuccess}
        />
      )}
    </div>
  );
}

export default LectureUserDetail;
